import { useParams } from 'react-router';
import { Navigate } from 'react-router-dom';
import { useDashboard, useOwnerPortalContact } from '@vrplatform/data';
import {
  OwnerStatementSummariesDetail,
  OwnerStatementSummariesDetailContextProvider,
} from '@vrplatform/embed';
import { Breadcrumbs } from '@vrplatform/components';
import { Group } from '@mantine/core';
import { GeneralLedgerExportButton } from '../../GeneralLedgerExportButton';
import { GeneralLedgerViewPageHeader } from '../../GeneralLedgerViewPageHeader';
import { day, isUUID } from '@vrplatform/utils';

type MonthRange = `${string}...${string}`;

const defaultRange =
  `${day().startOf('year').yyyymmdd()}...${day().endOf('year').yyyymmdd()}` satisfies MonthRange;

export const OwnerStatementSummariesDetailView = () => {
  'use memo';

  const [dashboard] = useDashboard();

  if (dashboard === 'owner') {
    return <Owner />;
  } else {
    return <PM />;
  }
};

const PM = () => {
  'use memo';

  const { contactId, monthRange } = useParams<{
    contactId: string;
    monthRange: string;
  }>();

  if (!contactId || !isUUID(contactId)) {
    return (
      <Navigate
        to={`/reports/statement-summaries/list/${monthRange || defaultRange}`}
        replace
      />
    );
  }

  return (
    <OwnerStatementSummariesDetailContextProvider
      contactId={contactId ?? undefined}
      monthRange={monthRange as MonthRange}
    >
      <GeneralLedgerViewPageHeader
        report="statement-summaries"
        titleTo={`/reports/statement-summaries/list/${monthRange}`}
      />
      <OwnerStatementSummariesDetail />
    </OwnerStatementSummariesDetailContextProvider>
  );
};

const Owner = () => {
  'use memo';

  const { contactId } = useOwnerPortalContact();
  const { monthRange } = useParams<{ monthRange: MonthRange }>();

  return (
    <OwnerStatementSummariesDetailContextProvider
      contactId={contactId ?? undefined}
      monthRange={monthRange || defaultRange}
    >
      <Group justify="space-between" mb="lg">
        <Breadcrumbs pageTitle="Statement Summary" />
        <GeneralLedgerExportButton report={'statement-summaries'} />
      </Group>

      <OwnerStatementSummariesDetail />
    </OwnerStatementSummariesDetailContextProvider>
  );
};
